import { AddOutlined } from '@mui/icons-material';
import { Box, CircularProgress } from '@mui/material';
import { Button } from '@mui/material';
import { DocumentStatus } from '@synergy/models';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import { PaloStatusPill, paloLinkProps, paloLinkReset } from '@synergy/components-palo';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import { ReactElement, ReactNode, useState } from 'react';
import { getDocumentStatusTranslationId, getPurchaseOrderStatusTranslationId } from '@synergy/utils';
import { useNavigate } from 'react-router-dom';

import { ConverterJobFactory } from '../convert-receipt/util/converter-job-factory';
import { ConverterReceiptModal } from '../convert-receipt/converter-receipt-modal';
import { DetailStream } from '../activity-sidebar/details/detail-stream';
import { DocumentChainStep } from '../../common/models/document-chain-step';
import { TransitionTarget } from '../convert-receipt/models/internal/transition-target';
import { useAppSelector } from '../../store/use-appstate-selector';
import { useDocumentChain } from './palo-document-chain-context';

const c = tokens.color;

/**
 * The stages in the order procurement runs through them. A chain never holds
 * all five — 337 of 105.205 hold four, none five — so the view shows the stages
 * that carry something rather than a fixed row of empty boxes.
 */
const STAGES: { route: string; labelId: string }[] = [
    { route: 'demands', labelId: 'app.document.demand' },
    { route: 'sales-orders', labelId: 'app.document.salesOrder' },
    { route: 'inquiries', labelId: 'app.document.inquiry' },
    { route: 'purchase-orders', labelId: 'app.document.purchaseOrder' },
    { route: 'invoices', labelId: 'app.document.invoice' },
];

/**
 * Which follow-up makes which stage. A credit note is an invoice, so the
 * invoice stage can be reached two ways and offers both (#2166).
 */
const TARGETS_BY_ROUTE: Record<string, TransitionTarget[]> = {
    inquiries: [TransitionTarget.Inquiry],
    'purchase-orders': [TransitionTarget.PurchaseOrder],
    invoices: [TransitionTarget.Invoice, TransitionTarget.Credit],
};

const CREATE_LABEL_BY_TARGET: Record<number, string> = {
    [TransitionTarget.Inquiry]: 'app.chain.create.inquiry',
    [TransitionTarget.PurchaseOrder]: 'app.chain.create.purchaseOrder',
    [TransitionTarget.Invoice]: 'app.chain.create.invoice',
    [TransitionTarget.Credit]: 'app.chain.create.credit',
};

/**
 * The follow-up that would fill this stage, if the document can still make one.
 * Only offered on a stage the chain is missing — an existing stage is reached
 * through the actions above, where it does not read as a gap to close.
 */
const createLabelFor = (
    stage: Stage,
    possibleTargets: TransitionTarget[]
): { target: TransitionTarget; labelId: string } | undefined => {
    if (!stage.missing) {
        return undefined;
    }

    const target = (TARGETS_BY_ROUTE[stage.route] ?? []).find(entry => possibleTargets.includes(entry));

    return target ? { target, labelId: CREATE_LABEL_BY_TARGET[target] } : undefined;
};

const DAY = 24 * 60 * 60 * 1000;

/**
 * The chain answers with the plain status string of whichever kind the document
 * is; the pill and the label read the same five vocabularies from it.
 */
const statusOf = (step: DocumentChainStep): DocumentStatus => step.status as DocumentStatus;

/** An order calls its invoiced state "Abgerechnet"; every other kind keeps the common word (#2264). */
const statusLabelOf = (step: DocumentChainStep): string =>
    'purchase-orders' === step.type
        ? getPurchaseOrderStatusTranslationId(statusOf(step))
        : getDocumentStatusTranslationId(statusOf(step));

/**
 * The one value a chain agrees on, or nothing.
 *
 * A chain does not have to be about one customer or one subject: 858 of the
 * 1.594 conversions between two invoices join invoices of different members —
 * one supplier's run of invoices, chained one to the next. Naming such a chain
 * after whichever member came first would put a wrong name on 297 documents.
 */
const agreedOn = (values: (string | null)[]): string | undefined => {
    const named = [...new Set(values.filter((value): value is string => null !== value))];

    return 1 === named.length ? named[0] : undefined;
};

interface Stage {
    route: string;
    labelId: string;
    steps: DocumentChainStep[];
    /** A stage that should be there and is not — the broken chain, shown. */
    missing?: boolean;
}

/**
 * The procurement chain of the open document as one view (#2160).
 *
 * The bracket over the document kinds from the design study: which stages the
 * process reached, what each of them holds, and how long it took. The data
 * model stays as it is — this reads the conversion log, it does not merge
 * anything.
 */
export const PaloDocumentChain = (): ReactElement => {
    const { steps, loading, currentKey, documentType, documentId, create } = useDocumentChain();
    const currency = useAppSelector(state => state.settings.currency);
    const navigate = useNavigate();
    const [creating, setCreating] = useState<TransitionTarget | null>(null);

    // What this document can still become. The factory answers it from the
    // document itself, which is also what the conversion dialog goes by — so
    // the tab cannot offer a follow-up the dialog would then refuse.
    const possibleTargets =
        create?.targets ??
        (create?.source ? ConverterJobFactory.CreateConverterJob(create.source)?.possibleTargets ?? [] : []);

    /* Every follow-up this document can still become, per the study's "Anlegen"
       in the links tab. A missing stage carries its own above; these are the
       ones whose stage already exists — or, on a document with no chain, all of
       them. */
    const createActions = create && possibleTargets.length > 0 && (
        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mb: '14px' }}>
            {possibleTargets.map(target => (
                <Button
                    key={target}
                    size="small"
                    variant="outlined"
                    startIcon={<AddOutlined />}
                    onClick={() => {
                        create.prepare?.();
                        setCreating(target);
                    }}
                >
                    <FormattedMessage id={CREATE_LABEL_BY_TARGET[target]} />
                </Button>
            ))}
        </Box>
    );

    /* The dialog can only be built from the source, so a document that fetches
       its own waits here until it has arrived. */
    const converter = create?.source && (
        <ConverterReceiptModal
            isVisible={null !== creating}
            source={create.source}
            preselectedTarget={creating ?? undefined}
            onClose={() => setCreating(null)}
            onConvert={create.onConvert}
        />
    );

    if (loading && 0 === steps.length) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                <CircularProgress size={18} />
            </Box>
        );
    }

    // One document is not a chain. Saying so is part of the job: more than half
    // of all invoices stand alone, because they come in through the supplier
    // import instead of from an order.
    if (steps.length < 2) {
        return (
            <PaloThemeProvider>
                <Box sx={{ padding: '16px 2px', fontSize: '12.5px', color: c.ink3 }}>
                    <FormattedMessage id="app.chain.single" />
                </Box>

                {/* A document with no chain yet is the one that needs the offer
                    most — saying "nothing followed" and stopping there would be
                    the least useful place to stop. */}
                {createActions}
                {converter}
            </PaloThemeProvider>
        );
    }

    const stages = toStages(steps);
    const byKey = new Map(steps.map(step => [step.key, step]));
    const kindOf = new Map(STAGES.map(stage => [stage.route, stage.labelId]));
    const subject = agreedOn(steps.map(step => step.title));
    const customer = agreedOn(steps.map(step => step.customer));

    return (
        <PaloThemeProvider>
            <Box sx={{ padding: '2px 2px 16px' }}>
                <Box
                    sx={{
                        fontSize: '10.5px',
                        fontWeight: 640,
                        letterSpacing: '.08em',
                        textTransform: 'uppercase',
                        color: c.ink3,
                        pb: '5px',
                        borderBottom: `1px solid ${c.line}`,
                        mb: '12px',
                    }}
                >
                    <FormattedMessage id="app.chain.title" />
                    {[customer, subject].filter(Boolean).map(part => (
                        <Box key={String(part)} component="span" sx={{ textTransform: 'none', letterSpacing: 0 }}>
                            {' · '}
                            {part}
                        </Box>
                    ))}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'stretch', flexWrap: 'wrap', gap: '2px', mb: '14px' }}>
                    {stages.map((stage, index) => (
                        <Box key={stage.route} sx={{ display: 'flex', alignItems: 'center' }}>
                            {index > 0 && (
                                <Box sx={{ width: 16, height: '1px', backgroundColor: c.lineHard, flexShrink: 0 }} />
                            )}
                            <StageCard
                                stage={stage}
                                currentKey={currentKey}
                                currency={currency}
                                createLabelId={createLabelFor(stage, possibleTargets)?.labelId}
                                onNavigate={to => navigate(to, { state: { opened: Date.now() } })}
                                onCreate={() => {
                                    const offer = createLabelFor(stage, possibleTargets);

                                    if (offer) {
                                        create?.prepare?.();
                                        setCreating(offer.target);
                                    }
                                }}
                            />
                        </Box>
                    ))}
                </Box>

                <Metrics steps={steps} stages={stages} currency={currency} />

                {stages
                    .filter(stage => stage.steps.length > 0)
                    .map(stage => (
                        <Box key={stage.route} sx={{ mt: '14px' }}>
                            <Box sx={{ fontSize: '11px', fontWeight: 600, color: c.ink2, mb: '4px' }}>
                                <FormattedMessage id={stage.labelId} />
                                {stage.steps.length > 1 && (
                                    <Box component="span" sx={{ color: c.ink3, fontWeight: 400 }}>
                                        {' '}
                                        {stage.steps.length}
                                    </Box>
                                )}
                            </Box>
                            {stage.steps.map(step => (
                                <DocumentRow
                                    key={step.key}
                                    step={step}
                                    depth={depthOf(step, byKey)}
                                    current={step.key === currentKey}
                                    currency={currency}
                                    href={`/${step.type}/${step.id}`}
                                    onNavigate={to => navigate(to, { state: { opened: Date.now() } })}
                                />
                            ))}
                        </Box>
                    ))}

                {createActions}
                {converter}

                {/* What happened across the whole procurement rather than on the
                    document in front of you (#2160). It sits under the documents
                    because it is the answer to "and then?", not to "what is
                    there". */}
                {undefined !== documentType && documentId && (
                    <Box sx={{ mt: '18px', border: `1px solid ${c.line}`, borderRadius: tokens.radius.small + 'px' }}>
                        <Box sx={{ padding: '9px 13px' }}>
                            <DetailStream
                                documentType={documentType}
                                documentId={documentId}
                                chain={true}
                                stages={[
                                    { label: <FormattedMessage id="app.stream.filter.all" /> },
                                    ...stages
                                        .filter(stage => stage.steps.length > 0)
                                        .map(stage => ({
                                            value: stage.route,
                                            label: <FormattedMessage id={stage.labelId} />,
                                        })),
                                ]}
                                originOf={(type, id) => {
                                    const step = byKey.get(`${type}:${id}`);
                                    const labelId = kindOf.get(type);

                                    return (
                                        <>
                                            {labelId && <FormattedMessage id={labelId} />}
                                            {step?.number ? ` ${step.number}` : ''}
                                        </>
                                    );
                                }}
                            />
                        </Box>
                    </Box>
                )}
            </Box>
        </PaloThemeProvider>
    );
};

/**
 * The tab's own label, per the prototype's "Belege 3". It reads the chain the
 * mask already loaded, so the number costs no second request — and it stays
 * away while there is nothing to count.
 */
export const PaloDocumentChainTabName = (): ReactElement => {
    const { steps } = useDocumentChain();

    return (
        <>
            <FormattedMessage id="app.chain.tab" />
            {steps.length > 1 && (
                <Box component="span" sx={{ color: c.ink3, fontWeight: 400, marginLeft: '5px' }}>
                    {steps.length}
                </Box>
            )}
        </>
    );
};

/**
 * Groups the documents into stages and adds the one stage the chain should
 * have and does not: an order that never became an invoice. Only that one —
 * an order made straight from a demand skipped the inquiry on purpose, and
 * marking that as a gap would cry wolf on a hundred thousand chains.
 */
const toStages = (steps: DocumentChainStep[]): Stage[] => {
    const stages: Stage[] = STAGES.map(stage => ({
        ...stage,
        steps: steps.filter(step => step.type === stage.route),
    })).filter(stage => stage.steps.length > 0);

    const invoices = stages.find(stage => 'invoices' === stage.route);
    const orders = stages.find(stage => 'purchase-orders' === stage.route);
    // A cancelled order is not a chain that broke off — it ended on purpose,
    // and nobody is waiting for its invoice.
    const awaited = orders?.steps.some(step => 'STATUS_CANCELED' !== step.status);

    if (!invoices && awaited) {
        stages.push({ route: 'invoices', labelId: 'app.document.invoice', steps: [], missing: true });
    }

    return stages;
};

/** How far a document sits from the start of its chain, for the indent. */
const depthOf = (step: DocumentChainStep, byKey: Map<string, DocumentChainStep>): number => {
    let depth = 0;
    let origin: string | undefined = step.previous[0];

    // The log holds no cycles, but a guard costs nothing and a hung mask costs
    // the afternoon.
    while (origin && depth < 20) {
        origin = byKey.get(origin)?.previous[0];
        ++depth;
    }

    return depth;
};

const StageCard = ({
    stage,
    currentKey,
    currency,
    createLabelId,
    onCreate,
    onNavigate,
}: {
    stage: Stage;
    currentKey?: string;
    currency: string;
    createLabelId?: string;
    onCreate?: () => void;
    onNavigate: (to: string) => void;
}): ReactElement => {
    const single = 1 === stage.steps.length ? stage.steps[0] : undefined;
    const holdsCurrent = stage.steps.some(step => step.key === currentKey);

    return (
        <Box
            sx={{
                minWidth: 148,
                padding: '7px 11px',
                borderRadius: tokens.radius.small + 'px',
                border: `1px ${stage.missing ? 'dashed' : 'solid'} ${holdsCurrent ? c.accent : c.lineHard}`,
                backgroundColor: stage.missing ? 'transparent' : holdsCurrent ? c.accentSoft : c.surface,
            }}
        >
            <Box sx={{ fontSize: '10.5px', color: c.ink3, mb: '3px' }}>
                <FormattedMessage id={stage.labelId} />
            </Box>

            {stage.missing &&
                (createLabelId ? (
                    // "keine Rechnung erfasst" becomes "Rechnung anlegen": the
                    // gap the view makes visible is where the next document is
                    // made.
                    <Button
                        size="small"
                        variant="text"
                        startIcon={<AddOutlined />}
                        onClick={onCreate}
                        sx={{ padding: '0 4px', minHeight: 22, fontSize: '11.5px' }}
                    >
                        <FormattedMessage id={createLabelId} />
                    </Button>
                ) : (
                    <Box sx={{ fontSize: '12px', color: c.ink3 }}>
                        <FormattedMessage id="app.chain.stage.none" />
                    </Box>
                ))}

            {single && (
                <>
                    <Box
                        // The number opens the document, Ctrl/⌘ or the middle
                        // button in a tab of its own (#2327).
                        {...paloLinkProps(`/${single.type}/${single.id}`, onNavigate)}
                        sx={{
                            ...paloLinkReset,
                            display: 'block',
                            fontVariantNumeric: 'tabular-nums',
                            fontSize: '12px',
                            fontWeight: 550,
                            color: c.accentHi,
                            mb: '3px',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        {single.number ?? <FormattedMessage id="app.convertReceipt.draft" />}
                    </Box>
                    <PaloStatusPill status={statusOf(single)} label={<FormattedMessage id={statusLabelOf(single)} />} />
                    {null !== single.netValue && (
                        <Box
                            sx={{
                                fontVariantNumeric: 'tabular-nums',
                                fontSize: '11.5px',
                                color: c.ink2,
                                mt: '3px',
                            }}
                        >
                            <FormattedNumber value={single.netValue} style="currency" currency={currency} />
                        </Box>
                    )}
                </>
            )}

            {!single && !stage.missing && (
                <>
                    <Box sx={{ fontSize: '12.5px', fontWeight: 620, color: c.ink, mb: '3px' }}>
                        <FormattedMessage id="app.chain.stage.count" values={{ count: stage.steps.length }} />
                    </Box>
                    <Box sx={{ fontSize: '10.5px', color: c.ink3 }}>
                        <FormattedMessage
                            id="app.chain.stage.open"
                            values={{ count: stage.steps.filter(step => !isSettled(step)).length }}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

/**
 * Only for the "still open" count on a stage that holds many documents. It asks
 * the raw status rather than the five-step scale, because the scale is the
 * customer's to configure and this number must not move with it.
 *
 * Cancelled and declined count as settled: they are finished with, and calling
 * a cancelled credit note "open" says someone still has to do something about
 * it — which was exactly how it read in the mask.
 */
const isSettled = (step: DocumentChainStep): boolean =>
    step.status.startsWith('STATUS_COMPLETED') ||
    'STATUS_CANCELED' === step.status ||
    'STATUS_DECLINED' === step.status;

const Metrics = ({
    steps,
    stages,
    currency,
}: {
    steps: DocumentChainStep[];
    stages: Stage[];
    currency: string;
}): ReactElement => {
    const dates = steps
        .map(step => (step.createdAt ? new Date(step.createdAt).getTime() : Number.NaN))
        .filter(time => !Number.isNaN(time));
    const days = dates.length > 1 ? Math.floor((Math.max(...dates) - Math.min(...dates)) / DAY) : 0;
    const people = new Set(steps.map(step => step.responsible).filter(Boolean));
    const messages = steps.reduce((total, step) => total + step.messageCount, 0);

    // The money is counted once, at the last stage that carries any: an order
    // and the invoice made from it are the same money, so adding both up would
    // report a chain as twice its worth.
    const valued = [...stages].reverse().find(stage => stage.steps.some(step => null !== step.netValue));
    const value = valued?.steps.reduce((total, step) => total + (step.netValue ?? 0), 0);

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '18px',
                padding: '8px 11px',
                borderRadius: tokens.radius.small + 'px',
                backgroundColor: c.surfaceAlt,
                border: `1px solid ${c.line}`,
            }}
        >
            <Metric label={<FormattedMessage id="app.chain.metric.runtime" />}>
                {days > 0 ? (
                    <FormattedMessage id="app.chain.metric.days" values={{ count: days }} />
                ) : (
                    // Converted documents carry the timestamp of the one they
                    // came from, down to the second — "0 Tage" would read as a
                    // missing number rather than as a same-day chain.
                    <FormattedMessage id="app.chain.metric.sameDay" />
                )}
            </Metric>
            <Metric label={<FormattedMessage id="app.chain.metric.people" />}>
                {people.size > 0 ? [...people].join(', ') : '—'}
            </Metric>
            {messages > 0 && (
                <Metric label={<FormattedMessage id="app.chain.metric.messages" />}>
                    <FormattedMessage id="app.chain.metric.outbound" values={{ count: messages }} />
                </Metric>
            )}
            {undefined !== value && valued && (
                <Metric label={<FormattedMessage id={`app.chain.metric.value.${valued.route}`} />}>
                    <FormattedNumber value={value} style="currency" currency={currency} />
                </Metric>
            )}
        </Box>
    );
};

const Metric = ({ label, children }: { label: ReactNode; children: ReactNode }): ReactElement => (
    <Box>
        <Box sx={{ fontSize: '10.5px', color: c.ink3 }}>{label}</Box>
        <Box sx={{ fontSize: '12.5px', fontWeight: 550, color: c.ink }}>{children}</Box>
    </Box>
);

const DocumentRow = ({
    step,
    depth,
    current,
    currency,
    href,
    onNavigate,
}: {
    step: DocumentChainStep;
    depth: number;
    current: boolean;
    currency: string;
    href: string;
    onNavigate: (to: string) => void;
}): ReactElement => (
    // A link, so Ctrl/⌘, the middle button and the context menu open the
    // document in a tab of its own (#2327).
    <Box
        {...paloLinkProps(href, onNavigate)}
        sx={{
            ...paloLinkReset,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            height: 34,
            padding: '0 10px',
            // Capped: a survey that produced two dozen inquiries, each recorded
            // from the one before it, walked the rows off to the right.
            paddingLeft: `${10 + Math.min(depth, 4) * 14}px`,
            borderBottom: `1px solid ${c.line}`,
            cursor: 'pointer',
            backgroundColor: current ? c.accentSoft : 'transparent',
            '&:hover': { backgroundColor: c.accentSoft },
        }}
    >
        <Box
            sx={{
                fontVariantNumeric: 'tabular-nums',
                fontSize: '12px',
                fontWeight: 550,
                color: c.accentHi,
                minWidth: 118,
            }}
        >
            {step.number ?? <FormattedMessage id="app.convertReceipt.draft" />}
            {step.isCredit && (
                <Box component="span" sx={{ color: c.ink3, fontWeight: 400 }}>
                    {' '}
                    <FormattedMessage id="app.document.credit" />
                </Box>
            )}
        </Box>
        <PaloStatusPill status={statusOf(step)} label={<FormattedMessage id={statusLabelOf(step)} />} />
        {step.createdAt && (
            <Box sx={{ fontSize: '11.5px', color: c.ink3, fontVariantNumeric: 'tabular-nums' }}>
                <FormattedDate value={step.createdAt} year="numeric" month="short" day="2-digit" />
            </Box>
        )}
        <Box sx={{ fontSize: '11.5px', color: c.ink3, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {[step.supplier, step.responsible].filter(Boolean).join(' · ')}
        </Box>
        {step.messageCount > 0 && (
            <Box sx={{ fontSize: '10.5px', color: c.ink3 }}>
                <FormattedMessage id="app.chain.metric.outbound" values={{ count: step.messageCount }} />
            </Box>
        )}
        {null !== step.netValue && (
            <Box
                sx={{
                    marginLeft: 'auto',
                    fontSize: '12.5px',
                    fontVariantNumeric: 'tabular-nums',
                    color: step.netValue < 0 ? c.crit : c.ink,
                }}
            >
                <FormattedNumber value={step.netValue} style="currency" currency={currency} />
            </Box>
        )}
    </Box>
);
